import type { ReactNode } from "react";

import type { IconaTema } from "@/lib/prima-pagina";

/** Le icone della prima pagina, ridisegnate dal foglio di simboli del
 *  disegno del 15/10/2026 (uploads/Prima pagina-html.zip): un tratto a
 *  1,5 in `currentColor`, angoli vivi, e un dettaglio nel rame del
 *  segnale (`stroke-segnale` / `fill-segnale`). Il colore lo dà chi le
 *  usa, di solito il tono del tema (`TONO_ICONA`).
 *
 *  Sempre decorative: accanto c'è sempre la parola che dicono. */
export type NomeIcona =
  | IconaTema
  | "paese"
  | "caso"
  | "editoriale"
  | "segnale"
  | "dossier"
  | "filtra"
  /* Dalla pagina del dispaccio (uploads/Pagina della aperto
     notizia-html.zip e Notizia articolo chiuso riservata-html.zip), stesso
     foglio di simboli: le entità, i comandi e i servizi. */
  | "persona"
  | "azienda"
  | "istituzione"
  | "luogo"
  | "collegamento"
  | "condividi"
  | "scarica"
  /* La stampante di «Stampa» sulla pagina dell'editoriale (26/10/2026):
     prima usava `scarica`, che dice un'altra cosa. Stesso tratto. */
  | "stampa"
  | "cerca"
  | "report"
  | "accedi"
  /* Da /servizi (uploads/servizi 01 oi.html), stesso foglio di simboli:
     le icone delle aree e dei servizi che mancavano. */
  | "organizzazione"
  | "focus"
  | "crisi"
  | "scenari"
  /* Da /servizi/monitoraggio (uploads/monitoraggio 01.html), stesso
     foglio di simboli: la prima voce del perimetro. */
  | "tema"
  /* Da /contatti (uploads/contatti.html), stesso foglio di simboli: il
     profilo «PMI» del modulo. */
  | "pmi";

const S = "stroke-segnale";
const F = "fill-segnale stroke-none";

const DISEGNI: Record<NomeIcona, ReactNode> = {
  energia: (
    <>
      <path d="M13.5 2.5L5.5 13.5h6l-1 8 8-11h-6z" />
      <path d="M11.5 13.5l-.5 4" className={S} />
    </>
  ),
  finanza: (
    <>
      <path d="M3.5 17.5l5-5 4 3 7-7.5" />
      <path d="M15 8h4.5v4.5" />
      <path d="M3.5 20.5h17" className={S} />
    </>
  ),
  esteri: (
    <>
      <path d="M9 3.5h6l5.5 5.5v6L15 20.5H9L3.5 15V9z" />
      <path d="M12 3.5L9 8v8l3 4.5L15 16V8z" />
      <path d="M3.5 12h17" className={S} />
    </>
  ),
  politica: (
    <>
      <path d="M5.5 11.5L9 7.5h6l3.5 4" />
      <path d="M12 7.5V4" />
      <path d="M3.5 11.5h17M6 14v4.5M10 14v4.5M14 14v4.5M18 14v4.5M3.5 20.5h17" />
      <rect x="11" y="3" width="3" height="2" className={F} />
    </>
  ),
  trasporti: (
    <>
      <path d="M3.5 9.5h17v10h-17z" />
      <path d="M7.5 9.5v10M11.5 9.5v10M15.5 9.5v10" />
      <path d="M12 9.5V5.5l2-2" className={S} />
    </>
  ),
  tecnologia: (
    <>
      <path d="M7 7h10v10H7z" />
      <path d="M10 3.5V7M14 3.5V7M10 17v3.5M14 17v3.5M3.5 10H7M3.5 14H7M17 10h3.5M17 14h3.5" />
      <rect x="10.5" y="10.5" width="3" height="3" className={F} />
    </>
  ),
  difesa: (
    <>
      <path d="M12 3l7.5 3v7l-3 4.5L12 21l-4.5-3.5-3-4.5V6z" />
      <path d="M12 7v10" className={S} />
    </>
  ),
  impresa: (
    <>
      <path d="M3.5 8.5h17v12h-17z" />
      <path d="M9 8.5v-3l1-1h4l1 1v3" />
      <path d="M3.5 13.5h17" className={S} />
    </>
  ),
  commercio: (
    <>
      <path d="M4 8.5h14l-3-3" />
      <path d="M20 15.5H6l3 3" className={S} />
    </>
  ),
  agricoltura: (
    <>
      <path d="M12 21V9" />
      <path d="M12 9L9 6.5v-3L12 6zM12 9l3-2.5v-3L12 6zM12 14.5l-3.5-2.5V9l3.5 2.5zM12 14.5l3.5-2.5V9L12 11.5z" />
      <path d="M8 21h8" className={S} />
    </>
  ),
  sanzioni: (
    <>
      <path d="M9 3.5h6l5.5 5.5v6L15 20.5H9L3.5 15V9z" />
      <path d="M7 7l10 10" className={S} />
    </>
  ),
  notizia: (
    <>
      <path d="M5.5 3.5h9l4 4v13h-13z" />
      <path d="M14.5 3.5v4h4M8.5 14.5h7M8.5 17.5h4.5" />
      <path d="M8.5 11.5h7" className={S} />
    </>
  ),
  paese: (
    <>
      <path d="M4 7.5l4-3 4 2 3.5-1.5 4 2.5-1 4 1.5 3-3 4.5-4.5-1-3 2.5-3.5-3 1-3.5-3-2.5z" />
      <rect x="10" y="9.5" width="3" height="3" className={F} />
    </>
  ),
  caso: (
    <>
      <path d="M2.5 12h19" />
      <path d="M6 9.5L8.5 12 6 14.5 3.5 12zM12 9.5l2.5 2.5-2.5 2.5L9.5 12z" className="fill-bg" />
      <path d="M18 9.5l2.5 2.5-2.5 2.5-2.5-2.5z" className="fill-segnale stroke-segnale" />
    </>
  ),
  editoriale: (
    <>
      <path d="M12 3.5l5.5 7L12 20.5l-5.5-10z" />
      <path d="M12 13.5v7" />
      <rect x="11" y="9" width="2" height="2" className={F} />
    </>
  ),
  segnale: (
    <>
      <path d="M14.5 20.5v-9M19.5 20.5v-14" />
      <path d="M4.5 20.5v-3.5M9.5 20.5v-6" className={S} strokeWidth={2.2} />
    </>
  ),
  dossier: (
    <>
      <path d="M3.5 6.5h6l2 2h9v12h-17z" />
      <path d="M3.5 11.5h17" />
      <rect x="6" y="14" width="5" height="2.5" className={F} />
    </>
  ),
  filtra: (
    <>
      <path d="M3.5 4.5h17l-6.5 8v6l-4 2v-8z" />
      <path d="M7.5 8.5h9" className={S} />
    </>
  ),
  persona: (
    <>
      <path d="M10.5 3.5h3l2 2v3l-2 2h-3l-2-2v-3z" />
      <path d="M4.5 20.5v-3l3-3h9l3 3v3" />
      <path d="M10.5 6.5h3" className={S} />
    </>
  ),
  azienda: (
    <>
      <path d="M3.5 20.5V10l5-3v3.5l5-3v3.5l7-3.5v13.5z" />
      <path d="M2.5 20.5h19" />
      <rect x="15.5" y="14" width="2.5" height="2.5" className={F} />
    </>
  ),
  istituzione: (
    <>
      <path d="M3.5 9L12 4l8.5 5z" />
      <path d="M6.5 11.5v6M10 11.5v6M14 11.5v6M17.5 11.5v6M3.5 20.5h17" />
      <rect x="11" y="6.3" width="2" height="2" className={F} />
    </>
  ),
  luogo: (
    <>
      <path d="M12 21l-6.5-8.5V7.5l3-3.5h7l3 3.5v5z" />
      <rect x="10.5" y="7.5" width="3" height="3" className={F} />
    </>
  ),
  collegamento: (
    <>
      <path d="M9 11L5 15v2l2 2h2l4-4M15 13l4-4V7l-2-2h-2l-4 4" />
      <path d="M9.5 14.5l5-5" className={S} />
    </>
  ),
  condividi: (
    <>
      <path d="M6 9.5L8.5 12 6 14.5 3.5 12zM18 3.5L20.5 6 18 8.5 15.5 6zM18 15.5l2.5 2.5-2.5 2.5-2.5-2.5z" />
      <path d="M8.5 11l7-4M8.5 13l7 4" className={S} />
    </>
  ),
  scarica: (
    <>
      <path d="M12 3.5v11M7.5 10l4.5 4.5 4.5-4.5" />
      <path d="M4 20.5h16" className={S} />
    </>
  ),
  stampa: (
    <>
      <path d="M7 8.5v-5h10v5M6.5 17h-3V8.5h17V17h-3" />
      <path d="M7 13.5h10v7H7z" className={S} />
    </>
  ),
  cerca: (
    <>
      <path d="M8 3.5h4l3.5 3.5v4L12 14.5H8L4.5 11V7z" />
      <path d="M14.5 14.5l6 6" className={S} />
    </>
  ),
  pmi: (
    <>
      <path d="M3.5 20.5V11l5 3v-3l5 3v-3l4 2.5V4.5h3v16z" />
      <rect x="7" y="16.5" width="3" height="4" className={F} />
    </>
  ),
  tema: (
    <>
      <path d="M9 3.5h6l5.5 5.5v6L15 20.5H9L3.5 15V9z" />
      <path d="M12 3.5v17H9L3.5 15V9L9 3.5z" className={F} />
    </>
  ),
  report: (
    <>
      <path d="M3.5 3.5v17h17" />
      <path d="M7.5 16.5v-4M11.5 16.5v-7M15.5 16.5v-5" />
      <path d="M19.5 16.5v-11" className={S} />
    </>
  ),
  organizzazione: (
    <>
      <path d="M12 3.5l2.5 2.5-2.5 2.5L9.5 6z" className="fill-segnale stroke-segnale" />
      <path d="M6 15l2.5 2.5L6 20l-2.5-2.5zM18 15l2.5 2.5L18 20l-2.5-2.5z" />
      <path d="M10.5 8l-3.5 6M13.5 8l3.5 6M8.5 17.5h7" />
    </>
  ),
  focus: (
    <>
      <path d="M9 3.5h6l5.5 5.5v6L15 20.5H9L3.5 15V9z" />
      <path d="M12 1.5v3M12 19.5v3M1.5 12h3M19.5 12h3" />
      <rect x="10.5" y="10.5" width="3" height="3" className={F} />
    </>
  ),
  crisi: (
    <>
      <path d="M12 3.5l9 16.5H3z" />
      <path d="M12 9.5v5" />
      <rect x="11" y="16.3" width="2" height="2" className={F} />
    </>
  ),
  scenari: (
    <>
      <path d="M2.5 18.5h19" />
      <path d="M7 18.5V15l3-3h4l3 3v3.5" className={S} />
      <path d="M12 5v3M4.5 9l2 2M19.5 9l-2 2" />
    </>
  ),
  accedi: (
    <>
      <path d="M13.5 3.5h7v17h-7" />
      <path d="M3.5 12h11M10.5 8l4 4-4 4" className={S} />
    </>
  ),
};

export function Icona({
  nome,
  className,
  tono,
}: {
  nome: NomeIcona;
  /** La misura: di serie 16 pixel (`size-4`), il disegno usa anche 18 e 20. */
  className?: string;
  /** Il colore del tratto, come valore CSS (`var(--p-…)`). Senza, eredita. */
  tono?: string;
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinejoin="miter"
      strokeLinecap="square"
      className={`shrink-0 overflow-visible ${className ?? "size-4"}`}
      style={tono ? { color: tono } : undefined}
    >
      {DISEGNI[nome]}
    </svg>
  );
}
